/**
 * Beschriftungen des Fragebogen-Moduls — Farbe und Verhalten hier, TEXT im
 * i18n-Katalog (`i18n/{de,en}/questionnaires.ts`).
 *
 * **Warum ein eigener Namespace und nicht `ui/labels.ts`?** `ui/labels.ts` ist
 * die GETEILTE Zustands-Schicht; ihre Status-Texte sind bereichsübergreifend
 * gemeint. Hier heisst `review` „Bereit zur Prüfung" und nicht „Feedback",
 * `approved` „Geprüft" und nicht „Freigegeben" — ein Domänen-Text gehört zur
 * Domäne. Dieselbe Begründung wie bei `datasets.status` (i18n/de/datasets.ts).
 *
 * **Warum überhaupt übersetzt?** Frühere Fassungen dieser Datei beriefen sich
 * auf die OUT-Liste in `docs/i18n.md` §2 („die Studio-Seiten bleiben deutsch").
 * Das war falsch: §2 zählt die OUT-Menge abschliessend auf — `EmailAutomation*`,
 * `ClaimReview`, `ImageLibrary`, `HeyGenStudio`, `Blog*` —, und „Fragebögen"
 * steht dort nicht. Das Modul hatte die Ausnahme auf sich selbst erweitert
 * (T9-Abnahme, Defekt D-12). Es ist damit ein IN-Bereich wie jeder andere.
 *
 * Die Trennung ist dieselbe wie in `ui/labels.ts`: Text aus dem Katalog der
 * aktiven Sprache, `BadgeStatus` und „läuft gerade" aus den Maps hier — die
 * Farbe ist eine Eigenschaft des Zustands und keine der Sprache.
 */
import { katalog } from "../i18n/lang";
import type { BadgeStatus } from "../ui";
import type { FragebogenStatus, Konfidenz, LaufFortschritt } from "./api";

export interface StatusInfo {
  /** Anzeige-Text in der aktiven Oberflächen-Sprache. */
  label: string;
  status: BadgeStatus;
  /** Läuft gerade etwas? Treibt das Polling und den Spinner. */
  laeuft: boolean;
}

/**
 * Der Status-Automat aus `app/questionnaires/models.py` — Farbe + Lauf-Flag.
 *
 * `rejected` ist NICHT `error`: „die Datei ist gelesen und gehört nicht in
 * dieses System" (ein Quality Agreement) verlangt eine andere Datei, `error`
 * verlangt einen Entwickler. Zwei Beschriftungen, zwei Farben — sie hier
 * zusammenzuziehen wäre der erste Schritt zur Verwechslung.
 *
 * Der Schlüssel-Typ ist an `FragebogenStatus` gebunden; weil der Text unten
 * über denselben Schlüssel aus dem Katalog kommt, ist ein Zustand ohne Text
 * (in `de/` oder `en/`) ein Compiler-Fehler und keine stille Lücke.
 */
export const FRAGEBOGEN_STATUS: Record<
  FragebogenStatus,
  { status: BadgeStatus; laeuft: boolean }
> = {
  uploaded: { status: "neutral", laeuft: true },
  profiling: { status: "run", laeuft: true },
  extracting: { status: "run", laeuft: true },
  matching: { status: "run", laeuft: true },
  review: { status: "info", laeuft: false },
  approved: { status: "success", laeuft: false },
  filled: { status: "success", laeuft: false },
  rejected: { status: "warning", laeuft: false },
  error: { status: "danger", laeuft: false },
};

export function statusInfo(status: string): StatusInfo {
  const eintrag = FRAGEBOGEN_STATUS[status as FragebogenStatus];
  if (!eintrag) return { label: status, status: "neutral", laeuft: false };
  return {
    label: katalog().questionnaires.status[status as FragebogenStatus],
    ...eintrag,
  };
}

/**
 * Der Text am laufenden Bogen — Stufe, und wo es einen gibt: der Zähler.
 *
 * „Antworten werden gesucht" steht bei einem 70-Fragen-Bogen minutenlang
 * unverändert da; ohne Zähler ist die Anzeige von „hängt" nicht zu
 * unterscheiden. Der Zähler kommt vom Server (`pipeline_state_json`) und wird
 * hier nur beschriftet — nicht geraten: fehlt er, bleibt es bei der Stufe.
 */
export function laufBeschriftung(
  status: string,
  fortschritt?: LaufFortschritt | null
): string {
  const info = statusInfo(status);
  if (!info.laeuft || !fortschritt || fortschritt.gesamt <= 0) return info.label;
  return katalog().questionnaires.stufeMitZaehler(
    info.label,
    fortschritt.erledigt,
    fortschritt.gesamt
  );
}

/** Konfidenz-Ampel. „low" ist bewusst rot: die Synthese vergibt sie, wenn die
 *  Kanon-Antwort die Frage nur streift — das ist ein Lese-Auftrag. */
export const KONFIDENZ_BADGE: Record<Konfidenz, BadgeStatus> = {
  high: "success",
  medium: "warning",
  low: "danger",
};

export function konfidenzInfo(k: Konfidenz): { label: string; status: BadgeStatus } {
  return { label: katalog().questionnaires.konfidenz[k], status: KONFIDENZ_BADGE[k] };
}

/**
 * Dieselbe Ampel als PUNKT — die Fassung, die in die Zeilenliste passt.
 *
 * Die Pille („mittel") kostete in jeder der 70 Zeilen ~70 px Breite und war
 * damit der Grund, warum Fragetext und Antwort-Segmente umbrachen. Die Farbe
 * trägt dieselbe Aussage; der Text steht im Tooltip und für Screenreader in
 * einem `sr-only`-Span.
 */
export const KONFIDENZ_PUNKT: Record<Konfidenz, string> = {
  high: "bg-success",
  medium: "bg-warning",
  low: "bg-danger",
};

/** Anker-Arten aus `extraction` — die Rückschreibe-Adresse. */
export function ankerArt(raw: string): string {
  const texte = katalog().questionnaires.ankerArt as Record<string, string>;
  return texte[raw] ?? raw;
}

/**
 * Alter eines „Stand"-Datums in Klartext.
 *
 * Konzept §6 (Stammdaten-Drift, belegt an 62 → 65 Mitarbeitern): „Faktenblatt-
 * Felder tragen ein Stand-Datum; die Review-Oberfläche zeigt das Alter an."
 * Das Datum allein reicht dafür nicht — niemand rechnet beim Prüfen im Kopf
 * nach, wie alt „19.06.2024" ist.
 */
export function alter(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const monate = Math.max(
    0,
    Math.round((Date.now() - d.getTime()) / (1000 * 60 * 60 * 24 * 30.44))
  );
  const t = katalog().questionnaires;
  if (monate < 1) return t.alterDiesenMonat;
  if (monate < 24) return t.alterMonate(monate);
  return t.alterJahre(Math.floor(monate / 12));
}

/** Ist dieses Gültig-bis-Datum vorbei? (Zertifikate laufen ab — Konzept §2.3.) */
export function abgelaufen(iso: string | null | undefined): boolean {
  if (!iso) return false;
  const d = new Date(iso);
  return !Number.isNaN(d.getTime()) && d.getTime() < Date.now();
}

/**
 * `q_question.position` (0-basiert) → die Nummer, die auf der Karte steht.
 *
 * Der Server zählt ab 0, die Oberfläche ab 1 (`FrageKarte`: `#{position + 1}`).
 * Jeder Text, der auf eine ANDERE Frage verweist, muss durch diese Funktion —
 * sonst zeigt die Begründung „Frage #9" auf die Karte, die sichtbar „#10"
 * heisst (Tester-Befund QSP-091). Sie steht hier und nicht als `+ 1` an der
 * Aufrufstelle, damit die Klasse greppbar ist.
 */
export function anzeigeNummer(position: number | null | undefined): number {
  return typeof position === "number" ? position + 1 : 0;
}

/** Deutsches Datum ohne Uhrzeit — Format bleibt in jeder Sprache deutsch
 *  (`docs/i18n.md` §2). */
export function datum(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleDateString("de-DE");
}
